// Peças compartilhadas pelas telas de Gestão do Realizado (tema, carregamento, cartões,
// tabela, filtros e formatação). Paleta e utilitários base vêm de ../estoque/ui.
import { useCallback, useEffect, useState } from "react";
import { EMPRESAS_REALIZADO } from "../../constants/gestaoRealizado";
import { btnStyle as btnStyleBase, formatBRL, inputStyle, palette } from "../estoque/ui";

export { formatBRL, palette };

const CHAVE_TEMA = "gestaoRealizado.tema";

export function useTema() {
  const [isLight, setIsLight] = useState(() => {
    try { return localStorage.getItem(CHAVE_TEMA) !== "escuro"; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem(CHAVE_TEMA, isLight ? "claro" : "escuro"); } catch { /* sem storage */ }
  }, [isLight]);
  return [isLight, setIsLight, palette(isLight)];
}

// Executa `buscar` sempre que as dependências mudam; `recarregar` força nova busca.
export function useCarregar(buscar, deps) {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [versao, setVersao] = useState(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const executar = useCallback(buscar, deps);
  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    setErro("");
    executar()
      .then((r) => { if (!cancelado) setDados(r); })
      .catch((e) => { if (!cancelado) setErro(e.message); })
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
  }, [executar, versao]);
  return { dados, carregando, erro, setErro, recarregar: () => setVersao((v) => v + 1) };
}

export const MESES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
export const ANO_ATUAL = new Date().getFullYear();
export const ANOS = [ANO_ATUAL - 2, ANO_ATUAL - 1, ANO_ATUAL, ANO_ATUAL + 1];

const fmtNum = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtCompacto = new Intl.NumberFormat("pt-BR", { notation: "compact", maximumFractionDigits: 1 });

// Valor de tabela: sem "R$", traço para zero.
export const formatValor = (v) => (v === null || v === undefined || Math.abs(v) < 0.005 ? "—" : fmtNum.format(v));
export const formatCompacto = (v) => `R$ ${fmtCompacto.format(v || 0)}`;
export const formatCompetencia = (iso) => (iso ? `${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");
export const formatData = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");

export function Card({ C, titulo, subtitulo, acoes, children, padding = 16 }) {
  return (
    <section style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding, minWidth: 0 }}>
      {(titulo || acoes) && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
          <div>
            {titulo && <div style={{ fontSize: 14, fontWeight: 700 }}>{titulo}</div>}
            {subtitulo && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{subtitulo}</div>}
          </div>
          {acoes && <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>{acoes}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Kpi({ C, rotulo, valor, detalhe, cor }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px", minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>{rotulo}</div>
      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6, color: cor || C.text, fontVariantNumeric: "tabular-nums" }}>{valor}</div>
      {detalhe && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{detalhe}</div>}
    </div>
  );
}

export function GradeKpis({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }}>{children}</div>;
}

export const estiloTabela = (C) => ({
  wrap: { overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 10 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th: { position: "sticky", top: 0, background: C.bgActive, color: C.muted, fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", padding: "8px 10px", textAlign: "left", whiteSpace: "nowrap", borderBottom: `1px solid ${C.border}` },
  thNum: { textAlign: "right" },
  td: { padding: "7px 10px", borderBottom: `1px solid ${C.border}`, verticalAlign: "top" },
  tdNum: { textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  total: { fontWeight: 800, background: C.rowAlt },
});

export function Badge({ cor, children, title }) {
  return (
    <span title={title} style={{ display: "inline-block", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: `${cor}1F`, color: cor, whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

export function Seletor({ C, rotulo, value, onChange, opcoes, largura }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
      {rotulo}
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{ ...inputStyle(C), minHeight: 36, width: largura, fontSize: 13 }}>
        {opcoes.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

export const opcoesAno = ANOS.map((a) => ({ value: a, label: String(a) }));
export const opcoesEmpresa = [{ value: "", label: "Todas" }, ...EMPRESAS_REALIZADO.map((e) => ({ value: e, label: e }))];

export function BarraFiltros({ children }) {
  return <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>{children}</div>;
}

export function Carregando({ C }) {
  return <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 13 }}>Carregando…</div>;
}

export function Vazio({ C, children }) {
  return <div style={{ padding: 32, textAlign: "center", color: C.muted, fontSize: 13 }}>{children}</div>;
}

export { AvisoErro, Modal } from "../estoque/ui";
export const btnStyle = btnStyleBase;
export { inputStyle };

export const botaoPequeno = (cor, secundario = false) => ({
  ...btnStyleBase(cor, secundario), padding: "7px 12px", minHeight: 34, fontSize: 12,
});
